import axios from 'axios';

// Use environment variable or default to production URL
const API_BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Simple in-memory cache
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Request interceptor - Add authentication headers
api.interceptors.request.use((config) => {
  // Try JWT token first (new auth)
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Fallback to API key (legacy auth)
    const apiKey = localStorage.getItem('apiKey');
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }
  }
  
  // Check cache for GET requests (skip if force refresh)
  if (config.method === 'get' && !config.params?._nocache) {
    const cacheKey = config.url + JSON.stringify(config.params || {});
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      config.adapter = () => Promise.resolve({
        data: cached.data,
        status: 200,
        statusText: 'OK (cached)',
        headers: {},
        config,
      });
    }
  }
  
  return config;
});

// Response interceptor - Handle token refresh and cache
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && response.status === 200) {
      const cacheKey = response.config.url + JSON.stringify(response.config.params || {});
      cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          });

          const { access_token } = response.data;
          localStorage.setItem('accessToken', access_token);

          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          clearAuth();
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      } else {
        const apiKey = localStorage.getItem('apiKey');
        if (!apiKey) {
          clearAuth();
          window.location.href = '/';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions
const clearAuth = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('permissions');
  localStorage.removeItem('apiKey');
  cache.clear();
};

const clearCache = () => {
  cache.clear();
};

// ==================== Auth API ====================
export const authAPI = {
  login: (username, password) => 
    api.post('/auth/login', { username, password }),
  logout: () => 
    api.post('/auth/logout').finally(() => clearAuth()),
  getCurrentUser: () => 
    api.get('/auth/me'),
  refreshToken: (refreshToken) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }),
  getAllPermissions: () => 
    api.get('/auth/permissions'),
};

// ==================== Users API ====================
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUserDetail: (id) => api.get(`/users/${id}`),
  updateUserStatus: (id, status, reason) => 
    api.patch(`/users/${id}/status`, null, { params: { status, reason } })
      .then(res => { clearCache(); return res; }),
  getUserActivityLogs: (id, params) => api.get(`/users/${id}/activity-logs`, { params }),
};

// ==================== Devices API ====================
export const devicesAPI = {
  getDevices: (params) => api.get('/devices', { params }),
  getDeviceDetail: (id) => api.get(`/devices/${id}`),
  getSerialStats: () => api.get('/devices/serial-stats'),
};

// ==================== Notifications API ====================
export const notificationsAPI = {
  sendNotification: (data) => 
    api.post('/notifications/send', data).then(res => { clearCache(); return res; }),
  getNotifications: (params) => api.get('/notifications', { params }),
  getBanners: (params) => api.get('/notifications/banners', { params }),
  getBanner: (id) => api.get(`/notifications/banners/${id}`),
  createBanner: (data) => 
    api.post('/notifications/banners', data).then(res => { clearCache(); return res; }),
  updateBanner: (id, data) => 
    api.patch(`/notifications/banners/${id}`, data).then(res => { clearCache(); return res; }),
  deleteBanner: (id) => 
    api.delete(`/notifications/banners/${id}`).then(res => { clearCache(); return res; }),
  getFAQs: (params) => api.get('/notifications/faqs', { params }),
  getFAQ: (id) => api.get(`/notifications/faqs/${id}`),
  createFAQ: (data) => 
    api.post('/notifications/faqs', data).then(res => { clearCache(); return res; }),
  updateFAQ: (id, data) => 
    api.patch(`/notifications/faqs/${id}`, data).then(res => { clearCache(); return res; }),
  deleteFAQ: (id) => 
    api.delete(`/notifications/faqs/${id}`).then(res => { clearCache(); return res; }),
};

// ==================== Policies API ====================
export const policiesAPI = {
  getPolicies: (params) => api.get('/policies', { params }),
  getPolicyDetail: (id) => api.get(`/policies/${id}`),
  createPolicy: (data) => 
    api.post('/policies', data).then(res => { clearCache(); return res; }),
  updatePolicy: (id, data) => 
    api.put(`/policies/${id}`, data).then(res => { clearCache(); return res; }),
  deletePolicy: (id) => 
    api.delete(`/policies/${id}`).then(res => { clearCache(); return res; }),
  getConsentStatus: (id) => api.get(`/policies/${id}/consent-status`),
  activatePolicy: (id) => 
    api.patch(`/policies/${id}/activate`).then(res => { clearCache(); return res; }),
};

// ==================== Analytics API ====================
export const analyticsAPI = {
  getNewUserAnalytics: () => api.get('/analytics/new-users'),
};

// ==================== Serial Keys API ====================
export const serialKeysAPI = {
  getSerialKeys: (params) => api.get('/serial-keys', { params }),
  getSerialKeyDetail: (id) => api.get(`/serial-keys/${id}`),
  createSerialKey: (data) => 
    api.post('/serial-keys', data).then(res => { clearCache(); return res; }),
  updateSerialKey: (id, data) => 
    api.put(`/serial-keys/${id}`, data).then(res => { clearCache(); return res; }),
  resetSerialKey: (id, data) => 
    api.post(`/serial-keys/${id}/reset`, data).then(res => { clearCache(); return res; }),
  getResetHistory: (id) => api.get(`/serial-keys/${id}/reset-history`),
};

// ==================== Admin Users API ====================
export const adminUsersAPI = {
  getAdminUsers: (params) => api.get('/admin-users', { params }),
  getAdminUserDetail: (id) => api.get(`/admin-users/${id}`),
  createAdminUser: (data) => 
    api.post('/admin-users', data).then(res => { clearCache(); return res; }),
  updateAdminUser: (id, data) => 
    api.put(`/admin-users/${id}`, data).then(res => { clearCache(); return res; }),
  changePassword: (id, newPassword) => 
    api.post(`/admin-users/${id}/change-password`, { new_password: newPassword }),
  deleteAdminUser: (id) => 
    api.delete(`/admin-users/${id}`).then(res => { clearCache(); return res; }),
  unlockAdminUser: (id) =>
    api.post(`/admin-users/${id}/unlock`).then(res => { clearCache(); return res; }),
  getAdminPermissions: (id) => api.get(`/admin-users/${id}/permissions`),
  grantPermission: (id, permissionKey, reason) => 
    api.post(`/admin-users/${id}/permissions/grant`, { 
      permission_key: permissionKey, 
      reason 
    }).then(res => { clearCache(); return res; }),
  revokePermission: (id, permissionKey, reason) => 
    api.post(`/admin-users/${id}/permissions/revoke`, { 
      permission_key: permissionKey, 
      reason 
    }).then(res => { clearCache(); return res; }),
};

// ==================== IP Whitelist API ====================
export const ipWhitelistAPI = {
  getIPWhitelist: (params) => api.get('/ip-whitelist', { params }),
  addIP: (data) => api.post('/ip-whitelist', data).then(res => { clearCache(); return res; }),
  updateIP: (id, data) => api.put(`/ip-whitelist/${id}`, data).then(res => { clearCache(); return res; }),
  deleteIP: (id) => api.delete(`/ip-whitelist/${id}`).then(res => { clearCache(); return res; }),
};

// ==================== Access Logs API ====================
export const accessLogsAPI = {
  getAccessLogs: (params) => api.get('/access-logs', { params }),
  getAccessLogDetail: (id) => api.get(`/access-logs/${id}`),
  getAccessLogStats: (params) => api.get('/access-logs/stats', { params }),
};

export { clearAuth, clearCache };
export default api;
